import { PCConsoleSegementList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { getPCConsoleMetricValues } from '@intelligence/pages/TopCharts/PCGames/_config/utils';

const { pcConsoleCascaderMetricsMap } = PCConsoleSegementList;

type FormItem = {
  type?: string;
  cascadeOptions?: cascadeOptions;
};
type cascadeOptionsChildItem = {
  value: string;
  disabled?: boolean;
};
type cascadeOptionsItem = {
  value: string;
  disabled?: boolean;
  children: cascadeOptionsChildItem[];
};

type cascadeOptions = cascadeOptionsItem[];
const SOURCEMAP: { [key: string]: string } = {
  mscience: 'Mscience',
  gsd: 'gsd',
  npd: 'npd',
  steamcharts: 'vginsights',
  vginsights: 'vginsights',
  /** Steam 页「Steam Store」等指标：详情接口 data_sources 与 cascader 分组 value 一致 */
  source_steam: 'source_steam',
  /** Alinea 等指标：同上 */
  alinea: 'alinea',
  famitsu: 'famitsu',
  newzoo: 'newzoo',
  newzoo_weight: 'newzoo',
  opinions: 'opinions',
  ampere: 'ampere',
  gamalytic: 'gamalytic',
};
const famitsuMap: { [key: string]: string } = {
  famitsu_sales: 'week_sales',
  famitsu_price: 'usd_price',
  famitsu_revenue: 'revenue',
  famitsu_cumulative_sales: 'total_sales',
  famitsu_cumulative_revenue: 'total_revenue',
};

export function changeMetricSelector(payload: string[], cascaderMetricsMap = pcConsoleCascaderMetricsMap) {
  const payloadTmp = payload;
  const metrics = payloadTmp?.map((item) => cascaderMetricsMap?.[item]);
  const metricValues = getPCConsoleMetricValues(metrics);
  return metricValues?.source_metrics || {};
}

export const resolvePcConsoleSelectorMetricKey = (
  selectorValue: string,
  cascaderMetricsMap: Record<string, any> = pcConsoleCascaderMetricsMap,
): string => cascaderMetricsMap?.[selectorValue]?.value || selectorValue;

export const normalizePcConsoleOrderDataSource = (
  metric: string,
  dataSource?: string,
): string => {
  if (metric.startsWith('gamalytic__')) return 'gamalytic';
  if (metric.startsWith('source_steam__')) return 'source_steam';
  if (metric.startsWith('alinea__')) return 'alinea';
  if (metric === 'steamfollowers' || metric === 'steamreviews') return 'vginsights';
  if (metric === 'pcu' || metric === 'acu') return 'steamcharts';
  return dataSource || '';
};

export const buildOrderMetricsFromSelectorValues = (
  selectorValues: string[] = [],
  sourceMetricsMap: Record<string, string[]> = {},
  cascaderMetricsMap: Record<string, any> = pcConsoleCascaderMetricsMap,
) =>
  selectorValues
    .map((selectorValue: string) => {
      const metricMeta = cascaderMetricsMap?.[selectorValue];
      const metricKey = resolvePcConsoleSelectorMetricKey(
        selectorValue,
        cascaderMetricsMap,
      );
      const sourceKey = Object.keys(sourceMetricsMap || {}).find(key =>
        sourceMetricsMap[key]?.includes(metricKey),
      );
      if (!sourceKey) return null;
      return {
        metric: metricKey,
        dataSource: normalizePcConsoleOrderDataSource(
          metricKey,
          metricMeta?.metricValue ?? metricMeta?.dataSource ?? sourceKey,
        ),
      };
    })
    .filter(Boolean);

export const buildOrderMetricsFromTableColumns = (
  tableColumns: Array<Record<string, any>> = [],
) =>
  tableColumns
    .filter(col => col?.value && !col?.disabled)
    .map(col => {
      const metricKey = col.value as string;
      const normalizedDataSource = normalizePcConsoleOrderDataSource(
        metricKey,
        col?.metricValue ?? col?.dataSource,
      );
      if (!normalizedDataSource) return null;
      return {
        metric: metricKey,
        dataSource: normalizedDataSource,
      };
    })
    .filter(Boolean);

export function findOptionByType(type: string, formList: FormItem[]) {
  return formList.find((item) => item.type === type)?.cascadeOptions || [];
}

export { buildOrderMetricsFromSourceMetrics } from '@intelligence/components/DownloadCommon/DownloadPage/utils/reorderCombineDetailDownloadMetrics';
export {
  buildOrderMetricsFromDetailSelectorValues,
  buildOrderMetricsFromPageMetricValues,
  toDetailMetricSelectorValues,
} from '@intelligence/components/DownloadCommon/DownloadPage/utils/reorderCombineDetailDownloadMetrics';

/**
 * 获取融合ID详情页All metric页面下载弹窗metric默认值
 * @param metricList 指标列表
 * @param formList 表单项列表
 * @returns 默认指标值数组
 */
export function getAllMetricsDeatilDefaultMetrics(
  metricList: { [key: string]: [] },
  formList: FormItem[],
  metricsOrder: string[] = [],
) {
  const options = findOptionByType('sourceMetricSelectorDetail', formList);
  if (!metricList) {
    return (
      options
        ?.map((item: cascadeOptionsItem) => {
          return item?.children?.map((i: cascadeOptionsChildItem) => i.value);
        })
        .flat(1) || []
    );
  }
  // console.log(metricList,options,'metricListmetricList');

  // 默认勾选完全跟随外部传入的 source_metrics：外面传什么，这里就回显什么
  // - 仅做：去重 + disabled 过滤
  // - 注意：Compare 下载弹窗可能把 Gamalytic 指标合并进其它数据源分组（例如 "Steam Data" 分组的 value 仍是 vginsights），
  //   这时不能只按 source key 定位分组，需要按 metric 在 options 中“反查”所属分组。
  const getOptionMetric = (optValue?: string) => optValue?.split('###')?.[1];

  const addMetricFromGroup = (group: cascadeOptionsItem | undefined, metric: string) => {
    if (!group?.children?.length) return false;
    const hit = group.children.find((child) => {
      if (child?.disabled) return false;
      return getOptionMetric(child?.value) === metric;
    });
    if (!hit?.value) return false;
    return hit.value;
  };

  const findGroupBySourceKey = (sourceKey: string) => {
    const realKey = SOURCEMAP[sourceKey] || sourceKey;
    return options?.find((item) => item.value === realKey);
  };

  const findGroupByMetric = (metric: string) => {
    return options?.find((group) => {
      return group?.children?.some((child) => getOptionMetric(child?.value) === metric);
    });
  };

  if (metricsOrder?.length) {
    const orderedValues: string[] = [];
    metricsOrder.forEach(metricKey => {
      const groupByMetric = findGroupByMetric(metricKey);
      const cascadeValue = addMetricFromGroup(groupByMetric, metricKey);
      if (cascadeValue && !orderedValues.includes(cascadeValue)) {
        orderedValues.push(cascadeValue);
      }
    });
    return orderedValues;
  }

  const defaultVal = new Set<string>();

  const addMetricFromGroupToSet = (
    group: cascadeOptionsItem | undefined,
    metric: string,
  ) => {
    const cascadeValue = addMetricFromGroup(group, metric);
    if (!cascadeValue) return false;
    defaultVal.add(cascadeValue);
    return true;
  };

  for (const key in metricList) {
    const metrics = metricList[key] || [];
    const groupByKey = findGroupBySourceKey(key);

    metrics.forEach((m: string) => {
      const metric = key === 'famitsu' ? famitsuMap[m] : m;

      const hitByKey = addMetricFromGroupToSet(groupByKey, metric);
      if (hitByKey) return;

      const groupByMetric = findGroupByMetric(metric);
      addMetricFromGroupToSet(groupByMetric, metric);
    });
  }

  return Array.from(defaultVal);
}

/**
 * 构建gamalytic的默认勾选指标
 * @param metricsOptions - 指标options
 * @param currentMetric - 页面当前勾选的指标
 * gamalytic默认勾选的指标
 */
const getCombineStatsSteadDataGamalyticDetailDefaultMetrics = (metricsOptions: any[], currentMetric: string) => {
  let metrics: string[];

  // 处理 gamalytic 数据源的默认勾选规则
  if (
    ['gamalytic__pcu', 'gamalytic__acu', 'gamalytic__steamfollowers', 'gamalytic__steamreviews'].includes(currentMetric)
  ) {
    // pcu、acu、followers、reviews相关指标，默认勾选 pcu、acu、steamfollowers 和 steamreviews
    metrics = [
      'gamalytic###gamalytic__pcu',
      'gamalytic###gamalytic__acu',
      'gamalytic###gamalytic__steamfollowers',
      'gamalytic###gamalytic__steamreviews',
    ];
  } else if (['gamalytic__est_revenue', 'gamalytic__est_units'].includes(currentMetric)) {
    // sales 相关指标，默认勾选 est_revenue 和 est_units
    metrics = ['gamalytic###gamalytic__est_revenue', 'gamalytic###gamalytic__est_units'];
  } else {
    // 其他情况，只勾选当前 segment 对应的指标
    metrics = (metricsOptions || [])
      .filter((item) => item?.value?.split('###')[1] === currentMetric)
      .map((item) => item?.value || '');
  }

  return metrics;
};

/**
 * 获取融合ID详情页下载弹窗metric默认值
 *
 * @param source 数据源
 * @param curSegment 当前分段
 * @param formList 表单项数组
 * @returns 默认指标数组
 */
export function getCombineDetailDefaultMetrics(
  source: string,
  curSegment: string,
  formList: FormItem[],
): string[] {
  const options = findOptionByType('sourceMetricSelectorDetail', formList);

  const findMapOptionByValue = (value: string) => options.find((i) => i.value === value);

  const mapOption = findMapOptionByValue(SOURCEMAP[source] ?? source);

  let defaultMetrics: string[] = [];

  if (['steamcharts', 'newzoo', 'vginsights', 'ampere'].includes(source)) {
    if (['pcu', 'acu', 'steamfollowers', 'steamreviews'].includes(curSegment)) {
      defaultMetrics = [
        'vginsights###pcu',
        'vginsights###acu',
        'vginsights###steamfollowers',
        'vginsights###steamreviews',
      ];
    } else if (['est_revenue', 'est_units'].includes(curSegment)) {
      defaultMetrics = ['vginsights###est_revenue', 'vginsights###est_units'];
    } else {
      defaultMetrics = (mapOption?.children || [])
        .filter((item) => item.value?.split('###')[1] === curSegment)
        .map((item) => item.value || '');
    }
  } else if (source === 'gamalytic') {
    // 处理 gamalytic 数据源的默认勾选规则
    defaultMetrics = getCombineStatsSteadDataGamalyticDetailDefaultMetrics(mapOption?.children || [], curSegment);
  } else if (mapOption?.children) {
    // 默认只勾选当前页面子 segment（如 source_steam__pcu），不要勾选该数据源分组下全部指标
    const matched = mapOption.children.filter(
      (item: cascadeOptionsChildItem) =>
        item.value?.split('###')[1] === curSegment,
    );
    defaultMetrics =
      matched.length > 0
        ? matched.map((item: cascadeOptionsChildItem) => item.value || '')
        : mapOption.children.map((item: cascadeOptionsChildItem) => item.value || '');
  } else if (options) {
    defaultMetrics =
      options
        ?.map((item: cascadeOptionsItem) => {
          return item?.children?.map((i: cascadeOptionsChildItem) => i.value);
        })
        .flat(1) || [];
  }

  return defaultMetrics;
}

// PC&Console 移除 VGI Steam Weighted Price
export const deleteVgiWeightPriceOption = (list: any) => {
  if (!list?.length) return;
  const cascadeOptions = list?.find((item: any) => item?.field === 'metrics')?.cascadeOptions;
  const vgiOptions = cascadeOptions?.find((item: any) => item?.value === 'vginsights')?.children;
  const weightedPriceIndex = vgiOptions?.findIndex(
    (item: any) => item?.value === 'vginsights###weighted_price',
  );
  if (weightedPriceIndex > -1) {
    vgiOptions.splice(weightedPriceIndex, 1);
  }
};
